import { create } from 'zustand'

import { ApiRequestError, errorMessage } from '@/lib/api'

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  variant: ToastVariant
  title: string
  description?: string
  /** An optional inline action, e.g. "Undo" or "View ticket". */
  action?: { label: string; onClick: () => void }
  duration: number
}

interface ToastState {
  toasts: Toast[]
  push: (toast: Omit<Toast, 'id' | 'duration'> & { duration?: number }) => string
  dismiss: (id: string) => void
  clear: () => void
}

let counter = 0

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  push: ({ duration, ...toast }) => {
    const id = `toast-${++counter}`
    // Errors stay until dismissed; anything else clears itself.
    const ttl = duration ?? (toast.variant === 'error' ? 8000 : 4000)
    set((state) => ({ toasts: [...state.toasts, { ...toast, id, duration: ttl }] }))
    return id
  },

  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}))

/** Imperative helpers, so callers outside React can raise a toast. */
export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'success', title, description }),
  error: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'error', title, description }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'info', title, description }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'warning', title, description }),

  /** Report a thrown value without the caller unwrapping it. */
  fromError: (error: unknown, title = 'Action failed') => {
    const fields = error instanceof ApiRequestError ? error.fieldErrors : []
    // "The request payload is invalid" on its own sends people hunting through
    // a form for the one value the server refused. The field is the whole
    // message; the envelope around it is not.
    const description = fields.length
      ? fields.map((f) => (f.field ? `${f.field}: ${f.message}` : f.message)).join(' · ')
      : errorMessage(error)

    return useToastStore.getState().push({ variant: 'error', title, description })
  },
}
